import { collection, onSnapshot, query, orderBy, limit } from "firebase/firestore";
import { db, auth } from "../lib/firebase";
import { RedTeamReview, Requirement, DraftSection } from "../types";

/**
 * Subscribes to the latest Evaluator Red Team review record for an opportunity
 */
export function subscribeToLatestRedTeamReview(
  opportunityId: string,
  callback: (review: RedTeamReview | null) => void,
  onError?: (err: Error) => void,
): () => void {
  const colRef = collection(db, "opportunities", opportunityId, "red_team_reviews");
  const q = query(colRef, orderBy("generatedAt", "desc"), limit(1));

  return onSnapshot(
    q,
    (snapshot) => {
      if (snapshot.empty || !snapshot.docs[0]) {
        callback(null);
        return;
      }

      const docSnap = snapshot.docs[0];
      const data = docSnap.data();

      const record: RedTeamReview = {
        id: docSnap.id,
        opportunityId,
        overallWeightedScore:
          typeof data.overallWeightedScore === "number" ? data.overallWeightedScore : 50,
        overallGrade: data.overallGrade || "C",
        executiveSummary: data.executiveSummary || "",
        keyRisks: Array.isArray(data.keyRisks) ? data.keyRisks : [],
        criteriaScores: Array.isArray(data.criteriaScores) ? data.criteriaScores : [],
        unsupportedClaims: Array.isArray(data.unsupportedClaims) ? data.unsupportedClaims : [],
        modelUsed: data.modelUsed || "",
        generatedAt: data.generatedAt || new Date().toISOString(),
        generatedBy: data.generatedBy || "",
        generatedByEmail: data.generatedByEmail || "",
      };

      callback(record);
    },
    (err) => {
      console.error(`Error subscribing to red team reviews for ${opportunityId}:`, err);
      if (onError) onError(err);
    },
  );
}

/**
 * Subscribes to all Evaluator Red Team review records for history/audit
 */
export function subscribeToOpportunityRedTeamReviews(
  opportunityId: string,
  callback: (reviews: RedTeamReview[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const colRef = collection(db, "opportunities", opportunityId, "red_team_reviews");
  const q = query(colRef, orderBy("generatedAt", "desc"));

  return onSnapshot(
    q,
    (snapshot) => {
      const records: RedTeamReview[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          opportunityId,
          overallWeightedScore:
            typeof data.overallWeightedScore === "number" ? data.overallWeightedScore : 50,
          overallGrade: data.overallGrade || "C",
          executiveSummary: data.executiveSummary || "",
          keyRisks: Array.isArray(data.keyRisks) ? data.keyRisks : [],
          criteriaScores: Array.isArray(data.criteriaScores) ? data.criteriaScores : [],
          unsupportedClaims: Array.isArray(data.unsupportedClaims) ? data.unsupportedClaims : [],
          modelUsed: data.modelUsed || "",
          generatedAt: data.generatedAt || new Date().toISOString(),
          generatedBy: data.generatedBy || "",
          generatedByEmail: data.generatedByEmail || "",
        };
      });
      callback(records);
    },
    (err) => {
      console.error(`Error subscribing to all red team reviews for ${opportunityId}:`, err);
      if (onError) onError(err);
    },
  );
}

/**
 * Invokes the phq_redTeamReview Cloud Function to simulate an adversarial evaluation panel
 */
export async function runRedTeamReview(params: {
  opportunityId: string;
  opportunityTitle?: string;
  client?: string;
  requirements: Requirement[];
  draftSections: DraftSection[];
  customCriteria?: Array<{
    id: string;
    title: string;
    weight: number;
    description?: string;
  }>;
}): Promise<RedTeamReview> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error("You must be logged in to run the Evaluator Red Team review.");
  }
  const token = await currentUser.getIdToken();

  const baseUrl =
    import.meta.env.VITE_FUNCTIONS_BASE_URL ||
    "https://us-central1-in-house-dev-mlc.cloudfunctions.net";
  const url = `${baseUrl.replace(/\/$/, "")}/phq_redTeamReview`;

  const payload = {
    opportunityId: params.opportunityId,
    opportunityTitle: params.opportunityTitle,
    client: params.client,
    requirements: params.requirements.map((r) => ({
      id: r.id,
      title: r.title,
      prompt: r.prompt,
      mandatory: Boolean(r.mandatory),
      category: r.category,
    })),
    draftSections: params.draftSections.map((s) => ({
      id: s.id,
      requirementId: s.requirementId,
      title: s.title,
      content: s.content,
      status: s.status,
    })),
    customCriteria: params.customCriteria,
  };

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = "Evaluator Red Team review failed";
    try {
      const errObj = JSON.parse(errorText) as { error?: string };
      if (errObj.error) errorMessage = errObj.error;
    } catch {
      if (errorText) errorMessage = errorText;
    }
    throw new Error(errorMessage);
  }

  const result = (await response.json()) as { success: boolean; review: RedTeamReview };
  if (!result.success || !result.review) {
    throw new Error("Evaluator Red Team failed to produce a valid review");
  }

  return result.review;
}
